import { ChangeEvent, useRef, useState } from "react"

import {
  AiHelpArticle,
  AiHelpArticleStatus,
  deleteAiHelpArticle,
  getAiHelpArticles,
  importAiHelpPdf,
  reindexAiHelpArticle,
  saveAiHelpArticle,
  updateAiHelpArticle
} from "@domain/ai_help"
import useTranslate from "@hooks/use_translate"
import AddIcon from "@mui/icons-material/Add"
import ArchiveIcon from "@mui/icons-material/Archive"
import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import LockIcon from "@mui/icons-material/Lock"
import RefreshIcon from "@mui/icons-material/Refresh"
import SyncIcon from "@mui/icons-material/Sync"
import UnarchiveIcon from "@mui/icons-material/Unarchive"
import UploadFileIcon from "@mui/icons-material/UploadFile"
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import dayjs from "dayjs"

import ArticleDrawer from "./ArticleDrawer"
import EditArticleDialog from "./EditArticleDialog"

export const AI_HELP_ARTICLES_KEY = ["ai-help-articles"]
const INDEXING_POLL_MS = 5000
/** Content older than this is flagged for review; the panel changes faster than help pages do. */
const STALE_AFTER_DAYS = 180

const isStale = (article: AiHelpArticle) =>
  article.status !== "ARCHIVED" &&
  dayjs().diff(
    dayjs(article.sourceDate ?? article.indexedAt ?? article.updatedAt),
    "day"
  ) > STALE_AFTER_DAYS

const STATUS_COLOR: Record<
  AiHelpArticleStatus,
  "default" | "info" | "success" | "error"
> = {
  INDEXING: "info",
  READY: "success",
  FAILED: "error",
  ARCHIVED: "default"
}

export default function ArticlesTab() {
  const { translate } = useTranslate()
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [importing, setImporting] = useState<string | null>(null)
  const [opened, setOpened] = useState<AiHelpArticle | null>(null)
  const [editing, setEditing] = useState<AiHelpArticle | "new" | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: AI_HELP_ARTICLES_KEY,
    queryFn: async () => {
      const response = await getAiHelpArticles()
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage || "failed")
      }
      return response.data
    },
    refetchInterval: (query) =>
      query.state.data?.some((article) => article.status === "INDEXING")
        ? INDEXING_POLL_MS
        : false
  })

  const run = async (
    call: () => Promise<{ isSuccess: boolean; errorMessage?: string }>,
    success: string
  ) => {
    const response = await call()
    if (!response.isSuccess) {
      showErrorToast(response.errorMessage || translate("ai_help_failed"))
      return false
    }
    showSuccessToast(translate(success))
    await queryClient.invalidateQueries({ queryKey: AI_HELP_ARTICLES_KEY })
    return true
  }

  const importFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ""
    for (const file of files) {
      setImporting(file.name)
      await run(() => importAiHelpPdf({ pdf: file }), "ai_help_imported")
    }
    setImporting(null)
  }

  const remove = (article: AiHelpArticle) => {
    if (
      window.confirm(
        translate("ai_help_delete_confirm").replace("{title}", article.title)
      )
    ) {
      void run(() => deleteAiHelpArticle(article._id), "ai_help_deleted")
    }
  }

  if (isLoading) {
    return <CircularProgress sx={{ alignSelf: "center" }} />
  }
  if (error || !data) {
    return <Alert severity="error">{translate("ai_help_load_failed")}</Alert>
  }

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1} alignItems="center">
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf"
          multiple
          hidden
          onChange={(event) => void importFiles(event)}
        />
        <Button
          variant="contained"
          startIcon={
            importing ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <UploadFileIcon />
            )
          }
          disabled={!!importing}
          onClick={() => fileInput.current?.click()}
        >
          {importing
            ? translate("ai_help_importing").replace("{file}", importing)
            : translate("ai_help_import")}
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setEditing("new")}
        >
          {translate("ai_help_new_article")}
        </Button>
        <Typography variant="caption" color="text.secondary" flex={1}>
          {translate("ai_help_import_hint")}
        </Typography>
        <Tooltip title={translate("ai_help_refresh")}>
          <IconButton onClick={() => void refetch()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{translate("ai_help_col_article")}</TableCell>
              <TableCell>{translate("ai_help_col_status")}</TableCell>
              <TableCell>{translate("ai_help_col_content")}</TableCell>
              <TableCell>{translate("ai_help_col_panel_path")}</TableCell>
              <TableCell>{translate("ai_help_col_usage")}</TableCell>
              <TableCell>{translate("ai_help_col_updated")}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    py={2}
                    textAlign="center"
                  >
                    {translate("ai_help_empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {data.map((article) => (
              <TableRow key={article._id} hover>
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    textAlign="left"
                    onClick={() => setOpened(article)}
                  >
                    {article.title}
                  </Link>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {[article.category, article.slug]
                      .filter(Boolean)
                      .join(" · ")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5}>
                    <Tooltip title={article.error ?? ""}>
                      <Chip
                        size="small"
                        color={STATUS_COLOR[article.status]}
                        label={translate(
                          `ai_help_status_${article.status.toLowerCase()}`
                        )}
                      />
                    </Tooltip>
                    {isStale(article) && (
                      <Tooltip
                        title={translate("ai_help_stale_hint").replace(
                          "{days}",
                          String(STALE_AFTER_DAYS)
                        )}
                      >
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={translate("ai_help_stale")}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {translate("ai_help_content_counts")
                      .replace("{sections}", String(article.sections.length))
                      .replace("{faqs}", String(article.faqs.length))
                      .replace(
                        "{images}",
                        String(
                          article.sections.reduce(
                            (sum, section) => sum + section.images.length,
                            0
                          )
                        )
                      )
                      .replace("{chunks}", String(article.chunkCount))}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} alignItems="center">
                    <Typography
                      variant="caption"
                      color={article.panelPath ? undefined : "warning.main"}
                    >
                      {article.panelPath || translate("ai_help_no_panel_path")}
                    </Typography>
                    {!!article.permissions?.length && (
                      <Tooltip title={article.permissions.join(", ")}>
                        <LockIcon fontSize="inherit" color="action" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {article.useCount
                      ? translate("ai_help_usage")
                          .replace("{count}", String(article.useCount))
                          .replace(
                            "{date}",
                            dayjs(article.lastUsedAt).format("DD.MM.YYYY")
                          )
                      : translate("ai_help_unused")}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {dayjs(article.updatedAt).format("DD.MM.YYYY HH:mm")}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Tooltip title={translate("ai_help_edit")}>
                    <IconButton
                      size="small"
                      onClick={() => setEditing(article)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate("ai_help_reindex")}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={
                          article.status === "INDEXING" ||
                          article.status === "ARCHIVED"
                        }
                        onClick={() =>
                          void run(
                            () => reindexAiHelpArticle(article._id),
                            "ai_help_reindexing"
                          )
                        }
                      >
                        <SyncIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={translate(
                      article.status === "ARCHIVED"
                        ? "ai_help_unarchive"
                        : "ai_help_archive"
                    )}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        void run(
                          () =>
                            updateAiHelpArticle({
                              _id: article._id,
                              archived: article.status !== "ARCHIVED"
                            }),
                          "ai_help_saved"
                        )
                      }
                    >
                      {article.status === "ARCHIVED" ? (
                        <UnarchiveIcon fontSize="small" />
                      ) : (
                        <ArchiveIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={translate("ai_help_delete")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => remove(article)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <ArticleDrawer article={opened} onClose={() => setOpened(null)} />
      <EditArticleDialog
        article={editing}
        onClose={() => setEditing(null)}
        onSave={(draft) => run(() => saveAiHelpArticle(draft), "ai_help_saved")}
      />
    </Stack>
  )
}
